import type { FC } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import InputText from '@components/InputText';
import Modal from '@components/Modal';
import { Button } from '@components/ui/button';

import type { TeamFormState } from '../types';

interface TeamFormModalProps {
  control: Control<TeamFormState>;
  isDirty: boolean;
  isDisabled: boolean;
  isEdit: boolean;
  isPending: boolean;
  isValid: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onToggleStatus?: () => void;
}

export const TeamFormModal: FC<TeamFormModalProps> = ({
  control,
  isDirty,
  isDisabled,
  isEdit,
  isPending,
  isValid,
  onClose,
  onSubmit,
  onToggleStatus,
}) => (
  <Modal
    open
    className="sm:max-w-[600px]"
    onClose={onClose}
    data-testid="team-form-modal"
    title={isEdit ? 'Edit team' : 'Create team'}
  >
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <InputText
            {...field}
            autoFocus
            maxLength={50}
            labelText="Team name"
            isRequired
            dataTestId="team-name-input"
            errors={fieldState.error?.message}
          />
        )}
      />
      <div className="mt-6 flex items-center justify-between">
        {isEdit && onToggleStatus ? (
          <Button
            type="button"
            variant={isDisabled ? 'success' : 'danger'}
            disabled={isPending || isDirty}
            title={isDirty ? 'Save or cancel your changes first' : undefined}
            data-testid="team-toggle-status"
            onClick={onToggleStatus}
          >
            {isDisabled ? 'Activate' : 'Disable'}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!isValid || isPending} data-testid="team-form-submit">
            Submit
          </Button>
        </div>
      </div>
    </form>
  </Modal>
);
